import type { UIMessage } from "ai"
import { executeTool, type ClientRenderResults, type ClientRenderTool, type MenuSynthRequest } from "./tools"
import type { LoomDoc, ToolOutputs } from "./storage"

export type SynthesisRequest = MenuSynthRequest

// Set one keyed entry on a tool-output map, returning a new ToolOutputs.
type ToolOutputEntryKey = "blockSvgs" | "diagramSvgs" | "quiltSvgs" | "planMarkdowns" | "quiltGrids"
function withToolOutputEntry<K extends ToolOutputEntryKey>(
  outputs: ToolOutputs,
  key: K,
  callId: string,
  value: ToolOutputs[K][string],
): ToolOutputs {
  return { ...outputs, [key]: { ...outputs[key], [callId]: value } }
}

// Per-tool side-effect handlers in the doc-update style: each takes the typed
// result + toolCallId + current doc and returns a new doc with the rendered
// output (or pendingOptions, for show_menu) folded in. Exhaustive over
// ClientRenderTool so adding a key to ClientRenderResults forces a new handler
// here. Trust boundary: the dispatch cast is safe because the producer
// (executeTool) attaches `satisfies ClientRenderResults["…"]`.
//
// The full {svg, artifact} object is cached so a tool result remains pinnable
// after a refresh — the artifact is what the scrapbook stores, independent of
// the SVG used for visual display.
export const TOOL_OUTPUT_HANDLERS: { [K in ClientRenderTool]: (r: ClientRenderResults[K], callId: string, doc: LoomDoc) => LoomDoc } = {
  render_block:         (r, id, d) => "svg" in r ? { ...d, toolOutputs: withToolOutputEntry(d.toolOutputs, "blockSvgs",   id, r) } : d,
  render_block_diagram: (r, id, d) => "svg" in r ? { ...d, toolOutputs: withToolOutputEntry(d.toolOutputs, "diagramSvgs", id, r) } : d,
  render_quilt:         (r, id, d) => "svg" in r ? { ...d, toolOutputs: withToolOutputEntry(d.toolOutputs, "quiltSvgs",   id, r) } : d,
  render_quilt_grid:    (r, id, d) => "thumbnails" in r ? { ...d, toolOutputs: withToolOutputEntry(d.toolOutputs, "quiltGrids", id, r.thumbnails) } : d,
  render_plan:          (r, id, d) => ({ ...d, toolOutputs: withToolOutputEntry(d.toolOutputs, "planMarkdowns", id, r.markdown) }),
  show_menu:            (r, _id, d) => ({ ...d, toolOutputs: { ...d.toolOutputs, pendingOptions: r.options } }),
}

const CLIENT_RENDER_TOOL_NAMES = new Set<string>(Object.keys(TOOL_OUTPUT_HANDLERS))
function isClientRenderTool(name: string): name is ClientRenderTool {
  return CLIENT_RENDER_TOOL_NAMES.has(name)
}

// Synthesize a tool call straight into the doc — no LLM round-trip.
//
// The transcript gains a user-message + assistant-message pair where the
// assistant message carries a dynamic-tool part already at output-available.
// The shape is structurally identical to what useChat receives back from a
// real model turn that ended with this tool call, so the existing transcript
// renderer (ToolVisual) and follow-up logic can't tell the difference.
//
// For client-render tools (render_*, show_menu) the rich result is folded
// into doc.toolOutputs via TOOL_OUTPUT_HANDLERS and the dynamic-tool part
// carries an {ok:true} stub — same convention as LLM-driven calls. For other
// tools the result is the full payload so ToolVisual can render it inline.
export function applySynthesis(doc: LoomDoc, req: SynthesisRequest): LoomDoc {
  const suffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const userMsgId = `synth-user-${suffix}`
  const assistantMsgId = `synth-asst-${suffix}`

  // Run the primary call + each chained call in sequence, threading project
  // mutations through. Each call gets its own dynamic-tool part on the same
  // assistant message — mirroring the multi-tool turns the LLM emits.
  let next = doc
  const calls: Array<{ toolName: typeof req.toolName; args: Record<string, unknown> }> = [
    { toolName: req.toolName, args: req.args },
    ...(req.chain ?? []),
  ]
  const parts: UIMessage["parts"] = []
  for (let i = 0; i < calls.length; i++) {
    const call = calls[i]
    const callId = `synth-${call.toolName}-${suffix}-${i}`
    const { result, updatedProject } = executeTool(call.toolName, call.args, next.project)
    if (updatedProject) next = { ...next, project: updatedProject }

    let assistantOutput: unknown = result
    if (isClientRenderTool(call.toolName)) {
      const handler = TOOL_OUTPUT_HANDLERS[call.toolName] as (r: unknown, id: string, d: LoomDoc) => LoomDoc
      next = handler(result, callId, next)
      assistantOutput = { ok: true }
    }

    parts.push({
      type: "dynamic-tool",
      toolName: call.toolName,
      toolCallId: callId,
      state: "output-available",
      input: call.args,
      output: assistantOutput,
    })
  }

  const userMessage: UIMessage = {
    id: userMsgId,
    role: "user",
    parts: [{ type: "text", text: req.text }],
  }
  const assistantMessage: UIMessage = {
    id: assistantMsgId,
    role: "assistant",
    parts,
  }

  return {
    ...next,
    transcript: [...next.transcript, userMessage, assistantMessage],
  }
}
